import { useMemo } from 'react'
import { useAccount, useReadContract, useReadContracts, useWriteContract } from 'wagmi'
import { maxUint256 } from 'viem'
import {
  CONTRACTS,
  CONTRACTS_CHAIN_ID,
  bondCertificateAbi,
  bondMarketAbi,
  erc20Abi,
  genesisBondingCurveAbi,
  type Offering,
} from '@/lib/contracts'
import { decodePositionRows } from '@/lib/contracts/position-rows'

/**
 * Live reads from the deployed BondMarket on BNB Smart Chain testnet.
 *
 * Everything here talks to the chain directly rather than through our API. For
 * bond state that is the right call: the contract IS the source of truth, and
 * routing through a backend would add an indexing lag and a second thing to be
 * wrong. The API stays responsible for aggregate protocol stats, which need
 * history the chain does not cheaply provide.
 *
 * Every call pins `chainId` explicitly. Without it wagmi uses the wallet's
 * current chain, so a user sitting on BSC would silently read a non-existent
 * contract and see an empty—but confident—UI.
 */

const chainId = CONTRACTS_CHAIN_ID

/** All offerings, with their full struct resolved in one multicall. */
/**
 * Apply the bCDX rebrand to an offering's label.
 *
 * `pairLabel` is written once in `createOffering` and the contract exposes setters for
 * `active` and `payoutRate` only — there is no way to edit it. Fixing it properly means
 * recreating every offering, which mints new ids and would break the `?term=` links the
 * deposit QR hands to a phone, for a purely descriptive string.
 *
 * So it is rewritten here, at the same boundary the token symbol is. This is a LABEL,
 * not a figure: it renames the same pair the contract is already pointing at, and every
 * number beside it still comes from the chain untouched. The proper fix is a
 * `setOfferingLabel` on the next contract deploy.
 */
function rebrandLabel(label: string): string {
  return label.replace(/\bCDX\b/g, 'bCDX')
}

export function useOfferings(enabled = true) {
  const idsQuery = useReadContract({
    address: CONTRACTS.bondMarket,
    abi: bondMarketAbi,
    functionName: 'getOfferingIds',
    chainId,
    query: { enabled },
  })

  const ids = (idsQuery.data as bigint[] | undefined) ?? []

  const detailQuery = useReadContracts({
    contracts: ids.map((id) => ({
      address: CONTRACTS.bondMarket,
      abi: bondMarketAbi,
      functionName: 'offerings',
      args: [id],
      chainId,
    })),
    query: { enabled: enabled && ids.length > 0 },
  })

  const offerings = useMemo<Offering[]>(() => {
    if (!detailQuery.data) return []
    return detailQuery.data
      .map((r) => {
        if (r.status !== 'success' || !r.result) return null
        const t = r.result as unknown as [
          bigint, `0x${string}`, `0x${string}`, bigint, bigint, boolean, string,
        ]
        return {
          id: t[0],
          paymentToken: t[1],
          payoutToken: t[2],
          lockDuration: t[3],
          payoutRate: t[4],
          active: t[5],
          pairLabel: rebrandLabel(t[6]),
        }
      })
      .filter((o): o is Offering => o !== null)
  }, [detailQuery.data])

  return {
    offerings,
    isLoading: idsQuery.isLoading || detailQuery.isLoading,
    isError: idsQuery.isError || detailQuery.isError,
    error: idsQuery.error ?? detailQuery.error,
    refetch: () => {
      void idsQuery.refetch()
      void detailQuery.refetch()
    },
  }
}

/** Contract-computed payout. Never derive this client-side — the chain decides. */
export function usePreviewPayout(offeringId: bigint | undefined, amount: bigint) {
  return useReadContract({
    address: CONTRACTS.bondMarket,
    abi: bondMarketAbi,
    functionName: 'previewPayout',
    args: offeringId !== undefined ? [offeringId, amount] : undefined,
    chainId,
    query: { enabled: offeringId !== undefined && amount > 0n },
  })
}

export function useMinPurchase(enabled = true) {
  return useReadContract({
    address: CONTRACTS.bondMarket,
    abi: bondMarketAbi,
    functionName: 'minPurchase',
    chainId,
    query: { enabled },
  })
}

/** Live price and supply of the standalone Genesis Bond curve on BSC Testnet. */
export function useGenesisBondingCurve(bondAmount: bigint) {
  const state = useReadContracts({
    contracts: [
      { address: CONTRACTS.genesisBondingCurve, abi: genesisBondingCurveAbi, functionName: 'currentPrice', chainId },
      { address: CONTRACTS.genesisBondingCurve, abi: genesisBondingCurveAbi, functionName: 'soldSupply', chainId },
      { address: CONTRACTS.genesisBondingCurve, abi: genesisBondingCurveAbi, functionName: 'priceScaleSupply', chainId },
      { address: CONTRACTS.genesisBondingCurve, abi: genesisBondingCurveAbi, functionName: 'initialPrice', chainId },
      { address: CONTRACTS.genesisBondingCurve, abi: genesisBondingCurveAbi, functionName: 'slope', chainId },
      { address: CONTRACTS.genesisBondingCurve, abi: genesisBondingCurveAbi, functionName: 'active', chainId },
      {
        address: CONTRACTS.genesisBondingCurve,
        abi: genesisBondingCurveAbi,
        functionName: 'quotePurchase',
        args: bondAmount > 0n ? [bondAmount] : undefined,
        chainId,
      },
      {
        address: CONTRACTS.genesisBondingCurve,
        abi: genesisBondingCurveAbi,
        functionName: 'quotePurchase',
        args: [1_000n * 10n ** 18n],
        chainId,
      },
    ],
  })
  const [price, sold, scale, initial, slope, active, quote, sampleQuote] = state.data ?? []
  const quoted = quote?.status === 'success'
    ? quote.result as readonly [bigint, bigint, bigint]
    : undefined
  const sampleQuoted = sampleQuote?.status === 'success'
    ? sampleQuote.result as readonly [bigint, bigint, bigint]
    : undefined
  const soldSupply = sold?.result as bigint | undefined
  const priceScaleSupply = scale?.result as bigint | undefined
  const initialPrice = initial?.result as bigint | undefined
  const curveSlope = slope?.result as bigint | undefined
  // Integral of the live linear curve from zero sold supply to the current
  // sold supply. These inputs all come directly from the proxy, so this stays
  // aligned with the current on-chain curve configuration.
  const curveCollected = soldSupply !== undefined && priceScaleSupply && initialPrice !== undefined && curveSlope !== undefined
    ? (soldSupply * initialPrice) / 10n ** 18n
      + (curveSlope * soldSupply * soldSupply) / (2n * priceScaleSupply * 10n ** 18n)
    : undefined
  return {
    currentPrice: price?.result as bigint | undefined,
    initialPrice,
    slope: curveSlope,
    active: active?.result as boolean | undefined,
    soldSupply,
    priceScaleSupply,
    curveCollected,
    referenceRemaining: soldSupply !== undefined && priceScaleSupply
      ? soldSupply < priceScaleSupply ? priceScaleSupply - soldSupply : 0n
      : undefined,
    paymentQuote: quoted?.[0] ?? 0n,
    quoteStartPrice: quoted?.[1] ?? 0n,
    quoteEndPrice: quoted?.[2] ?? 0n,
    nextThousandQuote: sampleQuoted?.[0] ?? 0n,
    isLoading: state.isLoading,
    // A zero amount deliberately skips the interactive quote on overview screens.
    // Do not turn that absent call into a false market-connectivity error.
    isError: state.isError || (bondAmount > 0n && quote?.status === 'failure'),
  }
}

/** Payment-token balance and the market's allowance over it, for the approve step. */
export function usePaymentToken(enabled = true) {
  const { address } = useAccount()

  const query = useReadContracts({
    contracts: [
      {
        address: CONTRACTS.usdt,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address ?? '0x0000000000000000000000000000000000000000'],
        chainId,
      },
      {
        address: CONTRACTS.usdt,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [address ?? '0x0000000000000000000000000000000000000000', CONTRACTS.bondMarket],
        chainId,
      },
      { address: CONTRACTS.usdt, abi: erc20Abi, functionName: 'symbol', chainId },
    ],
    query: { enabled: enabled && Boolean(address) },
  })

  const [bal, allow, sym] = query.data ?? []
  return {
    balance: (bal?.result as bigint | undefined) ?? 0n,
    allowance: (allow?.result as bigint | undefined) ?? 0n,
    symbol: (sym?.result as string | undefined) ?? 'USDT',
    isLoading: query.isLoading,
    refetch: query.refetch,
  }
}

export function useCdxBalance() {
  const { address } = useAccount()
  return useReadContract({
    address: CONTRACTS.cdx,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId,
    query: { enabled: Boolean(address) },
  })
}

/** Every bond the connected wallet owns, with live claimable amounts. */
export function useUserBonds() {
  const { address } = useAccount()

  const idsQuery = useReadContract({
    address: CONTRACTS.bondMarket,
    abi: bondMarketAbi,
    functionName: 'getBondIdsByOwner',
    args: address ? [address] : undefined,
    chainId,
    query: { enabled: Boolean(address) },
  })

  const ids = (idsQuery.data as bigint[] | undefined) ?? []

  // One multicall for the economic position, claimable amount, and certificate owner.
  const detailQuery = useReadContracts({
    contracts: ids.flatMap((id) => [
      {
        address: CONTRACTS.bondMarket,
        abi: bondMarketAbi,
        functionName: 'userBonds',
        args: [id],
        chainId,
      },
      {
        address: CONTRACTS.bondCertificate,
        abi: bondCertificateAbi,
        functionName: 'ownerOf',
        args: [id],
        chainId,
      },
      {
        address: CONTRACTS.bondMarket,
        abi: bondMarketAbi,
        functionName: 'getClaimable',
        args: [id],
        chainId,
      },
    ]),
    query: { enabled: ids.length > 0, refetchInterval: 15_000 },
  })

  const positions = useMemo(
    () => (detailQuery.data ? decodePositionRows(ids, detailQuery.data) : []),
    [detailQuery.data, ids],
  )

  return {
    positions,
    isLoading: idsQuery.isLoading || detailQuery.isLoading,
    isError: idsQuery.isError || detailQuery.isError,
    refetch: () => {
      void idsQuery.refetch()
      void detailQuery.refetch()
    },
  }
}

/** Writes. Each returns the tx hash so the caller can surface a receipt link. */
export function useBondActions() {
  const { writeContractAsync, isPending } = useWriteContract()

  return {
    isPending,

    approve: (amount: bigint = maxUint256) =>
      writeContractAsync({
        address: CONTRACTS.usdt,
        abi: erc20Abi,
        functionName: 'approve',
        args: [CONTRACTS.bondMarket, amount],
        chainId,
      }),

    /**
     * `minPayout` is the on-chain slippage floor. We submit 99% of the quote, so
     * an owner repricing the offering downward between quote and confirmation
     * reverts instead of silently handing the user a worse bond.
     */
    purchase: (offeringId: bigint, amount: bigint, minPayout: bigint) =>
      writeContractAsync({
        address: CONTRACTS.bondMarket,
        abi: bondMarketAbi,
        functionName: 'purchaseBond',
        args: [offeringId, amount, minPayout],
        chainId,
      }),

    claim: (bondId: bigint) =>
      writeContractAsync({
        address: CONTRACTS.bondMarket,
        abi: bondMarketAbi,
        functionName: 'claimBond',
        args: [bondId],
        chainId,
      }),

    claimAll: () =>
      writeContractAsync({
        address: CONTRACTS.bondMarket,
        abi: bondMarketAbi,
        functionName: 'claimAll',
        chainId,
      }),
  }
}
